import React from "react";
import Head from "next/head";
import { FrazzleLogo } from "@/funnel/icons";

/** Split-panel layout shared by the login and signup pages. */
export default function AuthLayout({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <Head>
        <title>{`${title} — FrazzleCRM`}</title>
      </Head>
      <div className="flex min-h-screen">
        <div className="relative hidden flex-1 items-center justify-center overflow-hidden bg-gradient-to-br from-indigo-600 via-indigo-500 to-purple-600 lg:flex">
          <div className="absolute -left-24 -top-24 h-96 w-96 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-32 -right-16 h-[28rem] w-[28rem] rounded-full bg-purple-400/20 blur-2xl" />
          <div className="relative z-10 max-w-md px-10 text-white">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
                <FrazzleLogo size={30} />
              </span>
              <div>
                <div className="text-2xl font-bold tracking-tight">
                  FrazzleCRM
                </div>
                <div className="text-[11px] font-medium uppercase tracking-[0.25em] text-indigo-200">
                  Funnel Mapper
                </div>
              </div>
            </div>
            <h1 className="mt-10 text-3xl font-bold leading-tight">
              Map it. Automate it.
            </h1>
            <p className="mt-4 text-[15px] leading-relaxed text-indigo-100">
              Draw your funnels and automations on one canvas — then make them
              real. The map is the automation.
            </p>
            <div className="mt-10 space-y-3 text-[13px] text-indigo-100/90">
              <div className="flex items-center gap-2.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                Funnels, flows, and GHL-style automation steps
              </div>
              <div className="flex items-center gap-2.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                Real email &amp; SMS automations on your own credentials
              </div>
              <div className="flex items-center gap-2.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                Live status of what&apos;s actually implemented
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center bg-slate-50 px-6">
          <div className="w-full max-w-sm">
            <div className="mb-8 flex items-center gap-2 lg:hidden">
              <FrazzleLogo size={26} />
              <span className="text-lg font-bold tracking-tight text-slate-800">
                FrazzleCRM
              </span>
            </div>
            {children}
          </div>
        </div>
      </div>
    </>
  );
}

export const authFieldClass =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-[14px] outline-none placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100";

export const authButtonClass =
  "w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60";
